import { OmniElement, OmniStyleElement, html, css, nothing } from 'omni-ui';
import { Om2IconMenu, Om2Table } from 'omni-campaign-ui';
import './campaign-card-list.js';
import { trackPortalEvent } from '../helpers/utils.js';

OmniStyleElement.register();
Om2IconMenu.register();
Om2Table.register();

const MODULE_DEFAULT_DATA = {
  title: 'Add Document',
  schema: {
    btnLabel: 'Attach Doc',
    docLabel: 'Document Name',
    docPlaceholder: 'Name your file',
    urlLabel: 'Document URL',
    urlPlaceholder: 'Enter URL',
  },
};

/**
 * @summary Class for AttachAssets
 * @description Module that allows users attach assets/documents to the step they are working on. Asks the users input for the title of the asset/document and the hyperlink to the asset/document.
 * @extends OmniElement
 */
export class CampaignAttachAssets extends OmniElement {
  /**
   * EmpathyMap Lit Properties
   * @property {String} env - Application environment
   * @property {Object} context - Values passed in from wrapper/router
   * @property {String} token - API JWT token
   * @property {String} campaignStepId - The ID of the campaign step
   * @property {Array} savedData - Data already saved in the API
   * @property {String} docName - string that will be used/saved as the assets label/title
   * @property {String} docUrl - string/url to the asset
   * @property {Array} attachedAssets - local state of the assets that can be changed which then is used to update the api and passed down to the child component {@link #CampaignCardList}
   * @property {Object} overview - indicates overview mode, the value is the associated data
   * @property {Array} tableData - data to show in the overview table
   *
   */
  static get properties() {
    return {
      env: { type: String },
      context: { type: Object },
      token: { type: String },
      savedData: { type: Array },
      docName: { type: String },
      docUrl: { type: String },
      attachedAssets: { type: Array },
      overview: { type: Object },
      tableData: { type: Array },
      moduleLocation: { type: String },
    };
  }

  /**
   * Attach Assets module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        .attach-asset-form-wrapper {
          display: flex;
          flex-flow: column wrap;
          width: 100%;
          height: 100%;
          padding: 2.14rem 2.86rem 4.29rem 2.86rem;
          margin: 0 0 2.143rem 0;
          border-radius: 0.71rem;
          box-shadow: 0 0.71rem 4.29rem -0.36rem rgba(var(--rgb-nav-one), 0.08);
          background-color: rgb(var(--rgb-core-light));
        }

        .omni form {
          display: flex;
          flex-flow: row wrap;
          justify-content: flex-start;
        }

        .omni .field-input {
          max-width: 21.5rem;
          margin-right: 2.14rem;
          flex: 1;
        }

        .omni .field:not(:last-child) {
          margin-bottom: 0;
        }

        .omni .label {
          opacity: 0.6;
          font-size: 0.79rem;
          font-weight: normal;
          line-height: normal;
          letter-spacing: 0.24px;
          color: rgb(var(--rgb-core-dark));
          margin: 0 0 0.57rem 0.5rem !important;
        }

        .omni .input {
          width: 100%;
          border-radius: 0.43rem;
          border: solid 1px rgb(var(--rgb-border));
          background-color: rgb(var(--rgb-core-medium-two));
        }

        .omni .input:focus {
          background-color: rgb(var(--rgb-core-light));
          box-shadow: none !important;
        }

        .omni omni-tooltip .button {
          height: 2.57rem;
          margin-left: -0.71rem;
        }

        @media (max-width: 770px) {
          .omni form {
            display: flex;
            flex-flow: column wrap;
            justify-content: flex-start;
          }
          .omni omni-tooltip .button {
            height: 2.57rem;
            margin-left: 0px;
          }
        }
      `,
    ];
  }

  /**
   * Initialize {@link #docUrl}, {@link #docName} and {@link #attachedAssets}
   */
  constructor() {
    super();
    this.docUrl = '';
    this.docName = '';
    this.attachedAssets = [];
  }

  /**
   *Assigns array {@link #savedData} that is passed down by the parent component to the property {@link #attachedAssets}. If there is no data we initialize it with an empty array.
   */
  connectedCallback() {
    super.connectedCallback();
    this.attachedAssets = [...(this.savedData?.documents || [])];
    this.moduleTitle ||= MODULE_DEFAULT_DATA.title;
    this.schema ||= MODULE_DEFAULT_DATA.schema;
    this.moduleLocation =
      new URL(window.location.href).searchParams.get('view') === 'Assets'
        ? 'assets page'
        : 'attach docs module';
  }

  /**
   * Returns an array of row data to be included in csv export
   */
  get csvData() {
    return this.tableData.map(x => ({
      type: 'document',
      name: x.docName,
      url: x.docUrl,
    }));
  }

  /**
   *
   * @param {Event} e - event used to prevent the page from reloading on form submit
   * @description function that handles and saves the user's input into a formatted object within {@link #attachedAssets}. It then resets {@link #docName} and {@link #docUrl} and invokes {@link #_assetChange}
   */
  _attachAsset(e) {
    e.preventDefault();
    if (this.docUrl.indexOf('http') !== 0) {
      this.docUrl = `https://${this.docUrl}`;
    } else if (this.docUrl.indexOf('https') !== 0) {
      this.docUrl = this.docUrl.replace('http', 'https');
    }
    this.attachedAssets = [
      ...this.attachedAssets,
      { docName: this.docName, docUrl: this.docUrl },
    ];
    this.docName = '';
    this.docUrl = '';
    trackPortalEvent('document', 'attach', this.moduleLocation);
    this._assetChange();
  }

  /**
   *
   * @param {Number} index - index of asset object within {@link #attachedAssets}
   * @description function that deletes an asset object from {@link #attachedAssets} and invokes {@link #_assetChange}
   */
  _deleteAsset(index) {
    const previous = [...this.attachedAssets];
    previous.splice(index, 1);
    this.attachedAssets = previous;
    this._assetChange();
  }

  /**
   * Fires an event that campaign-step-detail listens to letting it know that there has been a change in state/information.
   * We know the specific module the change occurred by passing the module uuid and how we want to save it, {@link _submitAttachments}, to the detail object.
   */
  _assetChange() {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: this._submitAttachments.bind(this),
      },
    });
  }

  /**
   * Submits and saves the data to our backend API under the correct campaign and module.
   * Allows for the developer to structure the data as they see fit and stored in the APIs key savedData
   */
  async _submitAttachments() {
    await this.save({ documents: this.attachedAssets });
  }

  renderOverview() {
    if (!this.tableData) {
      this.tableData = [];
      this.overview.forEach(module =>
        module.saved_data.documents.forEach(document =>
          this.tableData.push({ ...document, step_id: module.step_id }),
        ),
      );
    }

    if (this.tableData.length === 0)
      return html`<slot name="empty-overview"></slot>`;

    return html`
      <omni-style>
        <om2-table
          autosort
          .columns=${[
            {
              label: 'Document Name',
              key: 'docName',
              isSortable: true,
              isMain: true,
            },
            {
              label: 'Document URL',
              key: 'docUrl',
              isSortable: true,
            },
            {
              label: 'Actions',
              passthrough: true,
              template: val =>
                html`<td part="table-body-cell">
                  <om2-icon-menu
                    .options=${['Open step', 'Open document']}
                    iconId="omni:interactive:launch"
                    @change=${e => {
                      const action = e?.detail?.value?.label;
                      if (action === 'Open step') {
                        this.dispatchNewEvent('active-step-change', {
                          detail: { id: val.step_id },
                        });
                        trackPortalEvent('step', 'open', this.moduleLocation);
                      } else if (action === 'Open document') {
                        window.open(val.docUrl, '_blank');
                        trackPortalEvent('link', 'open', this.moduleLocation);
                      }
                    }}
                  >
                  </om2-icon-menu>
                </td>`,
            },
          ]}
          .data=${this.tableData}
        >
        </om2-table>
      </omni-style>
    `;
  }

  /**
   *
   * @returns main html template for {@link #CampaignAttachAssets}. Renders the form where users submit their inputs and the child component {@link #CampaignCardList} that takes {@link #attachedAssets} as data to render the rows.
   */
  render() {
    const nameKey = 'docName';
    const urlKey = 'docUrl';

    if (this.overview) return this.renderOverview();

    return html`
      <omni-style>
        <div class="attach-asset-form-wrapper">
          <h2 class="title mb-5 is-3">${this.moduleTitle}</h2>
          <form class="m-0" @submit=${this._attachAsset}>
            <div class="field field-input">
              <label class="label">${this.schema.urlLabel}</label>
              <div class="control">
                <input
                  class="input docUrl"
                  type="text"
                  .value=${this.docUrl}
                  placeholder=${this.schema.urlPlaceholder}
                  @input=${e => {
                    this.docUrl = e.target.value;
                  }}
                  required
                  ?disabled=${this.context.readonly}
                />
              </div>
            </div>
            <div class="field field-input">
              <label class="label">${this.schema.docLabel}</label>
              <div class="control">
                <input
                  class="input docName"
                  type="text"
                  .value=${this.docName}
                  placeholder=${this.schema.docPlaceholder}
                  @input=${e => {
                    this.docName = e.target.value;
                  }}
                  required
                  ?disabled=${this.context.readonly}
                />
              </div>
            </div>
            <div class="field">
              <label class="label is-invisible">Attach doc</label>
              <div class="control">
                <button
                  class="button is-outlined"
                  id="submit-btn"
                  ?disabled=${!(this.docUrl?.trim() && this.docName?.trim())}
                  type="submit"
                >
                  <span class="icon">
                    <omni-icon
                      class="is-size-1"
                      icon-id="omni:informative:check"
                    >
                    </omni-icon>
                  </span>
                  <span> ${this.schema.btnLabel}</span>
                </button>
              </div>
            </div>
          </form>
          <campaign-card-list
            .rowData="${this.attachedAssets}"
            .nameKey=${nameKey}
            .urlKey=${urlKey}
            .controlTemplate=${(doc, index) =>
              this.context.readonly
                ? nothing
                : html`
                    <omni-tooltip>
                      <button
                        slot="invoker"
                        class="button"
                        id=${index}
                        @click=${() => this._deleteAsset(index)}
                        ?disabled=${this.context.readonly}
                      >
                        <omni-icon
                          class="is-size-1"
                          icon-id="omni:interactive:trash"
                        ></omni-icon>
                      </button>
                      <div slot="content">Delete</div>
                    </omni-tooltip>
                    <omni-tooltip>
                      <button
                        slot="invoker"
                        class="button"
                        @click=${e => {
                          e.preventDefault();
                          window.open(doc?.docUrl, '_blank', 'noreferrer');
                          trackPortalEvent('link', 'open', this.moduleLocation);
                        }}
                      >
                        <omni-icon
                          class="is-size-1"
                          icon-id="omni:interactive:launch"
                        ></omni-icon>
                      </button>
                      <div slot="content">Open</div>
                    </omni-tooltip>
                  `}
          >
          </campaign-card-list>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-attach-assets', CampaignAttachAssets);
